import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearApplyBlocked } from "../../redux/actions/appActions";
import type { ApplyBlockedState } from "../../redux/actions/appActions";
import { buildApplyBlockedSummary, resolveMissingSectionRoute } from "../../utils/jobApplyValidation";
import {
  BG_SUBTLE,
  BORDER_LIGHT,
  FONT_FAMILY,
  PRIMARY,
  PRIMARY_DARK,
  PRIMARY_LIGHT,
  RADIUS_BUTTON,
  RADIUS_CARD,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from "../notifications/notificationTheme";

const dialogFont = { fontFamily: FONT_FAMILY };

const ApplyBlockedDialog = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const applyBlocked = useSelector((state: any) => state.app.applyBlocked) as ApplyBlockedState | null;

  const open = Boolean(applyBlocked?.missingSections?.length);
  const sections = applyBlocked?.missingSections ?? [];
  const summary =
    applyBlocked?.message && applyBlocked.message.length > 0
      ? applyBlocked.message
      : buildApplyBlockedSummary(sections);

  const handleClose = () => {
    dispatch(clearApplyBlocked());
  };

  const handleGoToSection = (section: (typeof sections)[0]) => {
    dispatch(clearApplyBlocked());
    navigate(resolveMissingSectionRoute(section));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          ...dialogFont,
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          ...dialogFont,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          fontSize: "18px",
          fontWeight: 600,
          color: TEXT_PRIMARY,
          pb: 1,
          pt: 2.5,
          px: 2.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: RADIUS_CARD,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: PRIMARY_LIGHT,
            flexShrink: 0,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 20, color: PRIMARY }} />
        </Box>
        Complete your profile
      </DialogTitle>
      <DialogContent sx={{ pt: 0, px: 2.5, ...dialogFont }}>
        <Typography
          sx={{
            fontSize: "14px",
            color: TEXT_SECONDARY,
            lineHeight: 1.5,
            mb: 2,
          }}
        >
          {summary}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: PRIMARY,
            mb: 1,
          }}
        >
          Missing sections ({sections.length})
        </Typography>
        <List
          disablePadding
          sx={{
            bgcolor: BG_SUBTLE,
            borderRadius: RADIUS_CARD,
            border: `1px solid ${BORDER_LIGHT}`,
            overflow: "hidden",
          }}
        >
          {sections.map((section) => (
            <ListItem
              key={`${section.key || section.label}-${section.route || ""}`}
              disablePadding
              divider
            >
              <ListItemButton
                onClick={() => handleGoToSection(section)}
                sx={{
                  py: 1.25,
                  "&:hover": { bgcolor: PRIMARY_LIGHT },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: PRIMARY,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={section.label}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: "14px",
                      fontWeight: 500,
                      color: TEXT_PRIMARY,
                      lineHeight: 1.35,
                    },
                  }}
                />
                <ArrowForwardIcon sx={{ fontSize: 16, color: PRIMARY }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, gap: 1, ...dialogFont }}>
        <Button
          onClick={handleClose}
          sx={{
            textTransform: "none",
            color: TEXT_SECONDARY,
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          Close
        </Button>
        {sections.length === 1 ? (
          <Button
            variant="contained"
            onClick={() => handleGoToSection(sections[0])}
            sx={{
              textTransform: "capitalize",
              fontSize: "14px",
              fontWeight: 600,
              bgcolor: PRIMARY,
              borderRadius: RADIUS_BUTTON,
              boxShadow: "none",
              px: 2.5,
              py: 1,
              "&:hover": { bgcolor: PRIMARY_DARK, boxShadow: "none" },
            }}
          >
            Complete {sections[0].label}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default ApplyBlockedDialog;
